const APP_KEY = 'kazang-pos';
const APPS_ENDPOINT = 'https://lna.pos.co.zm/SynergyApi/api/v1/usage/apps';
const USAGE_ENDPOINT = 'https://lna.pos.co.zm/SynergyApi/api/v1/usage/record';
const BATCH_ENDPOINT = 'https://lna.pos.co.zm/SynergyApi/api/v1/usage/record/batch';

let appKey = APP_KEY;
let userId = null;
let storeId = null;
let customerId = null;

// Queue for batched events
let eventQueue = [];
let batchTimeout = null;
const BATCH_DELAY = 2000; // Send batch after 2 seconds of inactivity

async function fetchAppKey() {
    try {
        const resp = await fetch(APPS_ENDPOINT);
        const apps = await resp.json();
        const match = apps.find(a => a.app_key === APP_KEY);
        if (match) {
            appKey = match.app_key;
            console.log('[UsageTracker] App key resolved:', appKey);
        }
    } catch (err) {
        console.warn('[UsageTracker] Could not fetch app key, using default:', APP_KEY);
    }
}

function getDeviceId() {
    let deviceId = localStorage.getItem('lytsoft_device_id');
    if (!deviceId) {
        deviceId = 'dev_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
        localStorage.setItem('lytsoft_device_id', deviceId);
    }
    return deviceId;
}

function sendToBackend(payload) {
    fetch(USAGE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    }).catch(err => console.warn('[UsageTracker] Failed:', err));
}

function processQueue() {
    if (eventQueue.length === 0) return;
    const batch = [...eventQueue];
    eventQueue = [];
    batchTimeout = null;

    fetch(BATCH_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(batch)
    }).catch(err => console.warn('[UsageTracker] Batch failed:', err));
}

function scheduleBatchProcess() {
    if (batchTimeout) clearTimeout(batchTimeout);
    batchTimeout = setTimeout(processQueue, BATCH_DELAY);
}

export function initUsageTracker(config = {}) {
    if (config.appKey) appKey = config.appKey;
    if (config.userId) userId = config.userId;
    if (config.storeId) storeId = config.storeId;
    if (config.customerId) customerId = config.customerId;
    // resolve app_key from API to ensure exact match with registered app
    fetchAppKey();
}

export function setUsageUser(user) {
    userId = user;
}

export function setUsageStore(store) {
    storeId = store;
}

export function setUsageCustomer(customer) {
    customerId = customer;
}

export function track(options = {}) {
    const payload = {
        app_key: appKey,
        user_id: options.userId || userId || null,
        device_id: getDeviceId(),
        feature: options.feature || null,
        action: options.action || 'view',
        customer_id: options.customerId || customerId || null,
        store_id: options.storeId || storeId || null,
    };

    // For page views and important events, send immediately
    if (options.immediate || options.action === 'view') {
        sendToBackend(payload);
    } else {
        // Queue other events for batch sending
        eventQueue.push(payload);
        scheduleBatchProcess();
    }
}

export function trackPageView(pageName, options = {}) {
    track({
        feature: 'page:' + pageName,
        action: 'view',
        ...options,
        immediate: true,
    });
}

export function trackButtonClick(buttonName, options = {}) {
    track({
        feature: 'btn:' + buttonName,
        action: 'click',
        ...options,
    });
}

export function trackFormSubmit(formName, options = {}) {
    track({
        feature: 'form:' + formName,
        action: 'submit',
        ...options,
        immediate: true,
    });
}

export function trackEvent(feature, action, options = {}) {
    track({ feature, action, ...options });
}

// Flush any pending events (call on logout/app close)
export function flushEvents() {
    if (batchTimeout) {
        clearTimeout(batchTimeout);
        batchTimeout = null;
    }
    processQueue();
}

// Also expose as window.usageTracker for quick access
window.usageTracker = {
    track,
    trackPageView,
    trackButtonClick,
    trackFormSubmit,
    trackEvent,
    init: initUsageTracker,
    setUser: setUsageUser,
    setStore: setUsageStore,
    setCustomer: setUsageCustomer,
    flush: flushEvents,
};

export default {
    initUsageTracker,
    track,
    trackPageView,
    trackButtonClick,
    trackFormSubmit,
    trackEvent,
    setUsageUser,
    setUsageStore,
    setUsageCustomer,
    flushEvents,
};
